import React, { useState, useEffect } from 'react';
import { TrendingUp, Plus, X, Search, Filter, Download, ArrowUp, ArrowDown, ArrowRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './StockMovement.css';

const StockMovement = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const REACT_APP_API_URL = process.env.REACT_APP_API_BASE_URL;

  const [showRecordForm, setShowRecordForm] = useState(false);
  const [movementError, setMovementError] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const [movementForm, setMovementForm] = useState({
    productId: '',
    sourceWarehouseId: '',
    destinationWarehouseId: '',
    type: 'transfer',
    quantity: '',
    inventoryItem: '',
    reorderPoint: '',
    supplierId: ''
  });

  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [level1Warehouses, setLevel1Warehouses] = useState([]);
  const [level2Warehouses, setLevel2Warehouses] = useState([]);

  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Fetch initial data
  useEffect(() => {
    fetchProducts();
    fetchWarehouses();
    fetchLevel1Warehouses();
    fetchSuppliers();
  }, []);

  // Fetch movements when products are loaded
  useEffect(() => {
    if(!token) return;
    if (products.length > 0) {
      fetchAllMovements();
    }
  }, [products, token]);

  const fetchProducts = async () => {
    try {
      // Load cached products if they exist
      const cached = sessionStorage.getItem("products");
      if (cached) {
        setProducts(JSON.parse(cached));
        return;
      }

      const res = await fetch(`${REACT_APP_API_URL}/Products`, {
        headers: {"Authorization": `Bearer ${token}`}
      });
      const data = await res.json();
      setProducts(data);
      sessionStorage.setItem("products", JSON.stringify(data));
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const cached = sessionStorage.getItem("warehouses");
      if (cached) {
        setWarehouses(JSON.parse(cached));
        return;
      }

      if(!token) return;
      const res = await fetch(`${REACT_APP_API_URL}/Warehouse`, {
        headers: {"Authorization": `Bearer ${token}`}
      });
      const data = await res.json();
      setWarehouses(data);
      sessionStorage.setItem("warehouses", JSON.stringify(data));
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };

  const fetchLevel1Warehouses = async () => {
    try {
      if(!token) return;
      const res = await fetch(`${REACT_APP_API_URL}/Warehouse/level1`, {
        headers: {"Authorization": `Bearer ${token}`}
      });
      const data = await res.json();
      setLevel1Warehouses(data);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };

  const fetchLevel2Warehouses = async (level1WarehouseId) => {
    try {
      if(!token) return;
      const res = await fetch(`${REACT_APP_API_URL}/Warehouse/${level1WarehouseId}/level2`, {
        headers: {"Authorization": `Bearer ${token}`}
      });
      const data = await res.json();
      setLevel2Warehouses(data);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const cached = sessionStorage.getItem("suppliers");
      if (cached) {
         
        setSuppliers(JSON.parse(cached));
        return;
      }

      if(!token) return;
      const res = await fetch(`${REACT_APP_API_URL}/suppliers`,{
        headers: {"Authorization": `Bearer ${token}`}
      });

      const data = await res.json();
       
      setSuppliers(data);
      sessionStorage.setItem("suppliers", JSON.stringify(data));
    }catch (err) {
      console.error('Error fetching suppliers: ', err);
    }
  }

    const loadInventory = async ({ force = false, token, REACT_APP_API_URL, setInventory }) => {
    try {
      if (!force) {
        const cached = sessionStorage.getItem("inventory");
        if (cached) {
          setInventory(JSON.parse(cached));
          return;
        }
      }

      const res = await fetch(`${REACT_APP_API_URL}/Inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to fetch inventory");

      const data = await res.json();
      sessionStorage.setItem("inventory", JSON.stringify(data));
      setInventory(data);
    } catch (err) {
      console.error("Failed to fetch inventory", err);
    }
  };

  useEffect(() => {
    if (!showRecordForm) return;

    loadInventory({
      force: false,
      token,
      REACT_APP_API_URL,
      setInventory
    });
  }, [showRecordForm, token]);


  const fetchAllMovements = async () => {
    setLoading(true);
    try {
      const cachedMovements = sessionStorage.getItem("StockMovements");

      if(cachedMovements){
        setMovements(JSON.parse(cachedMovements));
        return;
      }
      
      // Fetch movements for ALL products
      const allMovements = [];
      
      // Fetch movements for each product
      for (const product of products) {
        try {
          const productMovements = await fetchMovementsForProduct(product.id);
          allMovements.push(...productMovements);
        } catch (err) {
          console.error(`Error fetching movements for product ${product.id}:`, err);
        }
      }

      // Sort movements by date (newest first)
      const sortedMovements = allMovements.sort((a, b) => {
        const dateA = a.timestamp || a.createdAt || a.date;
        const dateB = b.timestamp || b.createdAt || b.date;
        return new Date(dateB) - new Date(dateA);
      });

       
      sessionStorage.setItem("StockMovements", JSON.stringify(sortedMovements));
      setMovements(sortedMovements);

      
    } catch (err) {
      console.error('Error fetching all movements:', err);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMovementsForProduct = async (productId) => {
    try {
      if(!token) return;
      const res = await fetch(`${REACT_APP_API_URL}/StockMovement/product/${productId}?page=1&pageSize=100`, {
        headers: {"Authorization": `Bearer ${token}`}
      });
      if (res.ok) {
        const data = await res.json();
        // Handle the actual response structure
        if (data && Array.isArray(data.movements)) {
          return data.movements;
        } else if (Array.isArray(data)) {
          return data;
        } else if (data && Array.isArray(data.items)) {
          return data.items;
        } else {
          console.warn('Unexpected response structure for product', productId, data);
          return [];
        }
      } else {
        console.warn('Failed to fetch movements for product:', productId, res.status);
        return [];
      }
    } catch (err) {
      console.error('Error fetching product movements:', err);
      return []; // Always return an array
    }
  };

  const fetchMovementsForWarehouse = async (warehouseId) => {
    try {
      if(!token) return;
      const res = await fetch(`${REACT_APP_API_URL}/StockMovement/warehouse/${warehouseId}?page=1&pageSize=100`, {
        headers: {"Authorization": `Bearer ${token}`}
      });
      if (res.ok) {
        const data = await res.json();
        // Handle the actual response structure
        if (data && Array.isArray(data.movements)) {
          return data.movements;
        } else if (Array.isArray(data)) {
          return data;
        } else if (data && Array.isArray(data.items)) {
          return data.items;
        } else {
          console.warn('Unexpected response structure:', data);
          return [];
        }
      } else {
        console.warn('Failed to fetch warehouse movements:', res.status);
        return [];
      }
    } catch (err) {
      console.error('Error fetching warehouse movements:', err);
      return []; // Always return an array
    }
  };

  const handleRecordMovement = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Convert frontend movement types to backend expected format
      const getBackendMovementType = (frontendType) => {
        switch (frontendType) {
          case 'stock-in':
            return 'stock_in';
          case 'stock-out':
            return 'stock_out';
          case 'transfer':
            return 'transfer';
          default:
            return frontendType;
        }
      };

      // Prepare base payload
      let payload = {
        productId: movementForm.productId,
        type: getBackendMovementType(movementForm.type),
        quantity: parseInt(movementForm.quantity),
        companyId: user.companyId
      };

      // Handle different movement types according to backend requirements
      switch (movementForm.type) {
        case 'transfer':
          // Transfer requires both source and destination
          if (!movementForm.sourceWarehouseId || !movementForm.destinationWarehouseId) {
            throw new Error('Both source and destination warehouses are required for transfers');
          }
          payload.sourceWarehouseId = movementForm.sourceWarehouseId;
          payload.destinationWarehouseId = movementForm.destinationWarehouseId;
          break;
        case 'stock-out':
          // Stock-in: only SOURCE warehouse required (goods coming FROM external)
          if (!movementForm.sourceWarehouseId) {
            throw new Error('Source warehouse is required for stock-in (where goods are coming from)');
          }
          payload.sourceWarehouseId = movementForm.sourceWarehouseId;
          // Destination can be null or empty for stock-in
          payload.destinationWarehouseId = movementForm.destinationWarehouseId || null;
          break;
        case 'stock-in':
          payload = {
            inventoryId: movementForm.inventoryItem,
            quantity: parseInt(movementForm.quantity),
            reorderPoint: parseInt(movementForm.reorderPoint),
            supplierId: movementForm.supplierId
          };
          break;

        default:
          throw new Error('Invalid movement type');
      }

      if (movementForm.type === "transfer") {
        const destInventoryExists = inventory.some(item => 
          item.productId === movementForm.productId &&
          item.warehouseId === movementForm.destinationWarehouseId
        );

        //if (!destInventoryExists) {
          //setMovementError("dest-warehouse-missing");
          //return; // Stop the record
       // }
      }

       

      if(!token) return;

      const endpoint =
      movementForm.type === "stock-in"
        ? `${REACT_APP_API_URL}/inventory/stock-in-with-qa`
        : `${REACT_APP_API_URL}/StockMovement/record`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }, [token]);

      if (movementForm.type === "transfer" && res.ok) {
        const res = await fetch(`${REACT_APP_API_URL}/ShopStockReceiving/create-receiving`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            shopId: movementForm.destinationWarehouseId,
            productId: movementForm.productId,
            supplierId: movementForm.sourceWarehouseId,
            expectedQuantity: parseInt(movementForm.quantity),
            unitCost: 0,
            reorderPoint: 0
          })
        });
      }

      const text = await res.text();
      let result = {};
      try {
        result = JSON.parse(text);
      } catch {
        result = { message: text };
      }

      setMovements(prev => {
        const updated = [...prev, result];

        // Update the sessionStorage cache
        sessionStorage.setItem("StockMovements", JSON.stringify(updated));

        return updated;
      });

      

      if (!res.ok || result.success === false) {
        if (result.message === "Stock movement recorded but inventory update failed.") {
          setMovementError("dest-warehouse-missing");
          return;
        }

        setError(result.message || "Unexpected error.");
        return;
      }
      
      setSuccess('Stock movement recorded successfully!');
      setMovementForm({
        productId: '',
        sourceWarehouseId: '',
        destinationWarehouseId: '',
        type: 'transfer',
        quantity: ''
      });
      setShowRecordForm(false);
      
      // Refresh ALL movements after recording
      sessionStorage.removeItem("StockMovements");
      fetchAllMovements();

      sessionStorage.removeItem("inventory");
      await loadInventory({
        force: true,
        token,
        REACT_APP_API_URL,
        setInventory
      });

    } catch (err) {
      console.error('Error recording movement:', err);
      setError('Failed to record stock movement: ' + err.message);
    }
  };

  // Update form when movement type changes
  const handleTypeChange = (newType) => {
    setMovementForm({
      ...movementForm,
      type: newType,
      // Reset warehouse selections when type changes
      sourceWarehouseId: '',
      destinationWarehouseId: ''
    });
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'stock_in':
      case 'stock-in':
        return <ArrowDown style={{ color: '#059669' }} />;
      case 'stock_out':
      case 'stock-out':
        return <ArrowUp style={{ color: '#dc2626' }} />;
      case 'transfer':
        return <ArrowRight style={{ color: '#2563eb' }} />;
      default:
        return <TrendingUp />;
    }
  };

  const getMovementLabel = (type) => {
    switch (type) {
      case 'stock_in':
      case 'stock-in':
        return 'Stock In';
      case 'stock_out':
      case 'stock-out':
        return 'Stock Out';
      case 'transfer':
        return 'Transfer';
      default:
        return type;
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? `${product.name} (${product.sku})` : productId;
  };

  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return '-';
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : warehouseId;
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Ensure movements is always an array before filtering
  const filteredMovements = Array.isArray(movements) ? movements.filter(movement => {
    const matchesSearch = 
      getProductName(movement.productId)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getWarehouseName(movement.sourceWarehouseId)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getWarehouseName(movement.destinationWarehouseId)?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || 
      movement.type === filterType || 
      (filterType === 'stock-in' && movement.type === 'stock_in') ||
      (filterType === 'stock-out' && movement.type === 'stock_out');
    return matchesSearch && matchesFilter;
  }) : [];

  const getProductsInInventoryForWarehouse = (warehouseId) => {
    return products.filter(p => 
      inventory.some(inv => inv.productId === p.id && inv.warehouseId === warehouseId)
    );
  };

  const getSupplierName= (supplierId) => {
    if (!supplierId) return '-';
    const supplier = suppliers.find(w => w.id === supplierId);
    return supplier ? supplier.name : "-" ;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header with user info and logout */}
      <div className='info-header'>
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>

      {/* Page Header */}
      <div>
      {/*<div style={{ backgroundColor: 'white', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', borderBottom: '1px solid #e5e7eb' }}>*/}
        <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/*<div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#002f06' }}>
                <TrendingUp style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>*/}
              <div>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Stock Movement</h1>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Track and manage stock movements</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '8px 16px', 
                  color: 'white', 
                  borderRadius: '8px', 
                  backgroundColor: '#6c757d', 
                  border: 'none', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <ArrowLeft style={{ width: '16px', height: '16px' }} />
                Back to Dashboard
              </button>
              <button
                onClick={() => setShowRecordForm(true)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '8px 16px', 
                  color: 'white', 
                  borderRadius: '8px', 
                  backgroundColor: '#F65A11', 
                  border: 'none', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <Plus style={{ width: '16px', height: '16px' }} />
                Record Movement
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error and Success Messages */}
      {(error || success) && (
        <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 24px', marginTop: '16px' }}>
          {error && (
            <div style={{ 
              backgroundColor: '#fef2f2', 
              border: '1px solid #fecaca', 
              color: '#dc2626', 
              padding: '12px 16px', 
              borderRadius: '8px',
              marginBottom: '8px'
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ 
              backgroundColor: '#f0fdf4', 
              border: '1px solid #bbf7d0', 
              color: '#166534', 
              padding: '12px 16px', 
              borderRadius: '8px',
              marginBottom: '8px'
            }}>
              {success}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '24px' }}>
        {/* Search and Filters */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
          border: '1px solid #e5e7eb',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#9ca3af' }} />
              <input className="filters"
                type="text"
                placeholder="Search by product or warehouse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
            </div>
            
            <select className='filters'
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                minWidth: '150px'
              }}
            >
              <option value="all">All Types</option>
              <option value="stock-in">Stock In</option>
              <option value="stock-out">Stock Out</option>
              <option value="transfer">Transfer</option>
            </select>

            <button
              onClick={fetchAllMovements}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                color: '#374151',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                border: '1px solid #d1d5db',
                cursor: 'pointer'
              }}
            >
              <Download style={{ width: '16px', height: '16px' }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Movements List */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', 
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <p style={{ color: '#6b7280' }}>Loading movements...</p>
            </div>
          ) : filteredMovements.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <TrendingUp style={{ width: '64px', height: '64px', margin: '0 auto', color: '#d1d5db', marginBottom: '16px' }} />
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>No stock movements found.</p>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                Record a movement or try refreshing to load movements.
              </p>
              <button
                onClick={() => setShowRecordForm(true)}
                style={{ 
                  padding: '8px 16px', 
                  color: 'white', 
                  borderRadius: '8px', 
                  backgroundColor: '#F65A11', 
                  border: 'none', 
                  cursor: 'pointer',
                  marginRight: '8px'
                }}
              >
                Record Movement
              </button>
              <button
                onClick={fetchAllMovements}
                style={{ 
                  padding: '8px 16px', 
                  color: '#F65A11', 
                  borderRadius: '8px', 
                  backgroundColor: 'transparent', 
                  border: '1px solid #F65A11', 
                  cursor: 'pointer'
                }}
              >
                Refresh
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 24px', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>Date & Time</th>
                    <th style={{ textAlign: 'left', padding: '12px 24px', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>Product</th>
                    <th style={{ textAlign: 'left', padding: '12px 24px', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '12px 24px', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>Source</th>
                    <th style={{ textAlign: 'left', padding: '12px 24px', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>Destination</th>
                    <th style={{ textAlign: 'left', padding: '12px 24px', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>Supplier</th>
                    <th style={{ textAlign: 'right', padding: '12px 24px', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMovements.map((movement, index) => (
                    <tr key={movement.id || index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#374151' }}>
                        {movement.timestamp ? new Date(movement.timestamp).toLocaleString() : 
                         movement.createdAt ? new Date(movement.createdAt).toLocaleString() : 
                         movement.date ? new Date(movement.date).toLocaleString() : 'N/A'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        {getProductName(movement.productId)}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getMovementIcon(movement.type)}
                          <span style={{ 
                            padding: '4px 8px', 
                            fontSize: '12px', 
                            fontWeight: '500', 
                            borderRadius: '12px',
                            backgroundColor: movement.type === 'stock_in' ? '#dcfce7' : movement.type === 'stock_out' ? '#fee2e2' : '#dbeafe',
                            color: movement.type === 'stock_in' ? '#166534' : movement.type === 'stock_out' ? '#991b1b' : '#1e40af'
                          }}>
                            {getMovementLabel(movement.type)}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#374151' }}>
                        {getWarehouseName(movement.sourceWarehouseId)}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#374151' }}>
                        {getWarehouseName(movement.destinationWarehouseId)}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#374151' }}>
                        {getSupplierName(movement.supplierId)}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', textAlign: 'right', fontWeight: '500', color: '#374151' }}>
                        {movement.quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showRecordForm && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '28rem',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Record Stock Movement</h3>
              <button onClick={() => setShowRecordForm(false)} style={{ color: '#9ca3af', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            <form onSubmit={handleRecordMovement} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* your form fields here */}
            </form>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {movementError === "dest-warehouse-missing" && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '28rem',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Stock movement error</h3>
              <button onClick={() => setMovementError(null)} style={{ color: '#9ca3af', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            <p>
              Alert: The destination warehouse you're trying to move stock to doesn't have a record for that product. Please create the inventory record first.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => navigate('/inventory')}
                style={{ width: '100%', padding: '8px 16px', color: 'white', borderRadius: '8px', backgroundColor: '#F65A11', border: 'none', cursor: 'pointer' }}
              >
                Create inventory record
              </button>
              <button
                type="button"
                onClick={() => setMovementError(null)}
                style={{ width: '100%', padding: '8px 16px', color: 'white', borderRadius: '8px', backgroundColor: '#898989ff', border: 'none', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Movement Modal */}
      {showRecordForm && (
        <div 
        className="movement-modal"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50,}}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '28rem', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Record Stock Movement</h3>
              <button 
                onClick={() => setShowRecordForm(false)} 
                style={{ color: '#9ca3af', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            
            <form onSubmit={handleRecordMovement} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Movement Type</label>
                <select
                  required
                  value={movementForm.type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                >
                  <option value="transfer">Transfer (Between Warehouses)</option>
                  <option value="stock-in">Stock In (Receive Goods)</option>
                  <option value="stock-out">Stock Out (Dispatch Goods)</option>
                </select>
              </div>

              

              {(movementForm.type === 'stock-in') && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Warehouse
                  </label>

                  <select
                    value={movementForm.sourceWarehouseId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setMovementForm({ ...movementForm, sourceWarehouseId: id });
                    }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  >
                    <option value="">Select Warehouse</option>
                    {level1Warehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(movementForm.type === 'stock-in') && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Inventory item</label>
                  <select
                    required
                    value={movementForm.inventoryItem}
                    onChange={(e) => setMovementForm({ ...movementForm, inventoryItem: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  >
                    <option value="">Select inventory item</option>
                    {inventory
                      .filter(item => item.warehouseId === movementForm.sourceWarehouseId)
                      .map(item => (
                        <option key={item.id} value={item.id}>
                          {item.productName} ({item.warehouseName})
                        </option>
                    ))}
                  </select>
                </div>
              )}

              {(movementForm.type === 'stock-in') && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Supplier</label>
                  <select
                    required
                    value={movementForm.supplierId}
                    onChange={(e) => setMovementForm({ ...movementForm, supplierId: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  >
                    <option value="">Select supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(movementForm.type === 'transfer' || movementForm.type === 'stock-out') && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Source Warehouse {movementForm.type === 'stock-in' && '(Where goods are coming from)'}
                  </label>

                  <select
                    value={movementForm.sourceWarehouseId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setMovementForm({ ...movementForm, sourceWarehouseId: id });
                      fetchLevel2Warehouses(id); // load level-2
                    }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  >
                    <option value="">Select Source Warehouse</option>
                    {level1Warehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(movementForm.type === 'transfer' || movementForm.type === 'stock-out') && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Product</label>
                  <select
                    required
                    value={movementForm.productId}
                    onChange={(e) => setMovementForm({ ...movementForm, productId: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  >
                    <option value="">Select Product</option>
                    {getProductsInInventoryForWarehouse(movementForm.sourceWarehouseId).map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} ({product.sku})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {movementForm.type === 'transfer' && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Destination Warehouse {movementForm.type === 'stock-out' && '(Where goods are going to)'}
                  </label>

                  <select
                    required
                    value={movementForm.destinationWarehouseId}
                    onChange={(e) =>
                      setMovementForm({ ...movementForm, destinationWarehouseId: e.target.value })
                    }
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  >
                    <option value="">Select Destination Warehouse</option>

                    {level2Warehouses.map(w => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}


              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Quantity</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={movementForm.quantity}
                  onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                  placeholder="Enter quantity"
                />
              </div>

              {(movementForm.type === 'stock-in') && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Reorder point</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={movementForm.reorderPoint}
                    onChange={(e) => setMovementForm({ ...movementForm, reorderPoint: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                    placeholder="Enter reorder point"
                  />
                </div>
              )}
              <button
                type="submit"
                style={{ width: '100%', padding: '8px 16px', color: 'white', borderRadius: '8px', backgroundColor: '#F65A11', border: 'none', cursor: 'pointer' }}
              >
                Record Movement
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockMovement;